import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import {
  hydrateProcessingFactoryFilterLabels,
} from '#/api/momMgmt/settings/processingFactory/fetchSelectOptions';
import useSideDetail from '#/hooks/useSideDetail';
import { createAsyncFilterLabelCache } from '#/modules/commonComponents/table/asyncFilterLabelCache';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';
import { hydrateWarehouseFilterLabels } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import WorkOrderSideMsg from '#/modules/momMgmt/workOrder/components/WorkOrderSideMsg';
import WorkOrderStockOpsPageHeader from '#/modules/momMgmt/workOrder/components/WorkOrderStockOpsPageHeader';
import { buildWorkOrderColumns } from '#/modules/momMgmt/workOrder/data/workOrderColumns';
import { getWorkOrderApiErrorMessage } from '#/modules/momMgmt/workOrder/helper/apiError';
import { applyScheduledStartDateRangeToFilters } from '#/modules/momMgmt/workOrder/helper/scheduledStartDateListFilter';
import useWorkOrderList from '#/modules/momMgmt/workOrder/hooks/useWorkOrderList';

const WORK_ORDER_SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  workOrderNo: '工單號',
  scheduledStartDate: '排程日',
  status: '狀態',
  mtr: '成品',
  bom: 'BOM',
  processingFactory: '加工廠',
  workWarehouse: '工作倉',
  targetWarehouse: '目標倉',
  qtySummary: '製造數量',
  remark: '備註',
};

const WorkOrderListPage = () => {
  const listRef = useRef(null);
  const [processingFactoryLabelCache] = useState(createAsyncFilterLabelCache);
  const [workWarehouseLabelCache] = useState(createAsyncFilterLabelCache);
  const [targetWarehouseLabelCache] = useState(createAsyncFilterLabelCache);
  const [labelCacheVersion, setLabelCacheVersion] = useState(0);
  const { displayData, setSelectedData } = useSideDetail();
  const canCreate = useCan(PERM.WORK_ORDER_CREATE);
  const canUpdate = useCan(PERM.WORK_ORDER_UPDATE);

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
    setListQuery,
  } = useWorkOrderList();

  const setScheduledStartDateRangeFilter = useCallback((range) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      filters: applyScheduledStartDateRangeToFilters(prev.filters, range),
    }));
  }, [setListQuery]);

  const handleFilterChange = useCallback((key, value) => {
    if (key === 'scheduledStartDateFrom' && value === undefined) {
      setScheduledStartDateRangeFilter(undefined);
      return;
    }
    setFilter(key, value);
  }, [setFilter, setScheduledStartDateRangeFilter]);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      hydrateProcessingFactoryFilterLabels(
        listQuery.filters?.processingFactoryId,
        processingFactoryLabelCache,
      ),
      hydrateWarehouseFilterLabels(
        listQuery.filters?.workWarehouseId,
        workWarehouseLabelCache,
      ),
      hydrateWarehouseFilterLabels(
        listQuery.filters?.targetWarehouseId,
        targetWarehouseLabelCache,
      ),
    ]).then((results) => {
      if (!cancelled && results.some(Boolean)) {
        setLabelCacheVersion((version) => version + 1);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    listQuery.filters?.processingFactoryId,
    listQuery.filters?.workWarehouseId,
    listQuery.filters?.targetWarehouseId,
    processingFactoryLabelCache,
    workWarehouseLabelCache,
    targetWarehouseLabelCache,
  ]);

  const columns = useMemo(
    () => {
      void labelCacheVersion;
      return buildWorkOrderColumns({
        filters: listQuery.filters || {},
        onFilterChange: handleFilterChange,
        onScheduledStartDateRangeChange: setScheduledStartDateRangeFilter,
        processingFactoryLabelCache,
        workWarehouseLabelCache,
        targetWarehouseLabelCache,
        canShowEdit: (row) => canUpdate && row.status === 'DRAFT',
      });
    },
    [
      listQuery.filters,
      handleFilterChange,
      setScheduledStartDateRangeFilter,
      processingFactoryLabelCache,
      workWarehouseLabelCache,
      targetWarehouseLabelCache,
      labelCacheVersion,
      canUpdate,
    ],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
    [columns, listQuery.filters],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowClick = useCallback((row, isSelected) => {
    if (isSelected) {
      setSelectedData(row.original);
      return;
    }
    setSelectedData(null);
  }, [setSelectedData]);

  const headerAction = canCreate ? (
    <ListAddButton to={ROUTES.workOrderCreate} />
  ) : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <WorkOrderStockOpsPageHeader page="workOrder" variant="list" />
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              ref={listRef}
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={isError ? getWorkOrderApiErrorMessage(error, '載入列表失敗') : undefined}
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋工單號、成品或備註"
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={WORK_ORDER_SORT_FIELD_LABELS}
              onFilterChange={handleFilterChange}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
              onSortingChange={handleSortingChange}
              headerAction={headerAction}
            />
          </div>

          <WorkOrderSideMsg displayData={displayData} />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default WorkOrderListPage;
